import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Video,
  CheckCircle,
  XCircle,
  Star,
  RefreshCcw,
  AlertCircle,
  Film,
  MessageSquare,
} from 'lucide-react'
import { sessionsApi } from '../../lib/api/sessions'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import EmptyState from '../../components/ui/EmptyState'

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'upcoming' | 'completed'

interface InterviewItem {
  source: 'booking' | 'session'
  source_id: number
  title: string
  interviewer_alias: string | null
  interviewer_id: number | null
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url: string | null
  zoom_password: string | null
  cancellation_reason: string | null
  created_at: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  const d = new Date(iso)
  return {
    day: d.getDate(),
    month: d.toLocaleString('default', { month: 'short' }).toUpperCase(),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    full: d.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    }),
  }
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Confirmed', scheduled: 'Scheduled', in_progress: 'In Progress',
  completed: 'Completed', cancelled: 'Cancelled', pending_payment: 'Awaiting Payment',
  payment_expired: 'Payment Expired', payment_failed: 'Payment Failed',
}
const STATUS_COLOR: Record<string, string> = {
  confirmed: '#16a34a', scheduled: '#2563eb', in_progress: '#7c3aed',
  completed: '#16a34a', cancelled: '#6b7280', pending_payment: '#d97706',
  payment_expired: '#dc2626', payment_failed: '#dc2626',
}

const STAR_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

// ── Component ─────────────────────────────────────────────────────────────────

export default function ClientInterviews() {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<Tab>('upcoming')
  const [items, setItems] = useState<InterviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState({ upcoming: 0, completed: 0 })

  // Rated tracking: "session-{id}" | "booking-{id}"
  const [ratedKeys, setRatedKeys] = useState<Set<string>>(new Set())

  // Reschedule modal
  const [rescheduleTarget, setRescheduleTarget] = useState<InterviewItem | null>(null)
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [rescheduling, setRescheduling] = useState(false)

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState<InterviewItem | null>(null)
  const [cancelling, setCancelling] = useState(false)

  // Recording modal
  const [recordingTarget, setRecordingTarget] = useState<InterviewItem | null>(null)
  const [recordingData, setRecordingData] = useState<{
    recording_url: string; transcript_url: string | null; duration_seconds: number | null
  } | null>(null)
  const [recordingLoading, setRecordingLoading] = useState(false)
  const [recordingError, setRecordingError] = useState('')

  // Rating modal
  const [rateTarget, setRateTarget] = useState<InterviewItem | null>(null)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [ratingComment, setRatingComment] = useState('')
  const [submittingRating, setSubmittingRating] = useState(false)
  const [ratingError, setRatingError] = useState('')

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchTab = useCallback(async (tab: Tab) => {
    setLoading(true)
    try {
      const data = await sessionsApi.getClientInterviews(tab)
      const list: InterviewItem[] = Array.isArray(data) ? data : []
      setItems(list)
      setCounts((prev) => ({ ...prev, [tab]: list.length }))
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchTab(activeTab) }, [activeTab, fetchTab])

  // Check which completed items are already rated
  useEffect(() => {
    if (activeTab !== 'completed' || items.length === 0) return
    Promise.all(
      items.map(async (item) => {
        try {
          const rated =
            item.source === 'session'
              ? await sessionsApi.getRatingForSession(item.source_id)
              : await sessionsApi.getRatingForBooking(item.source_id)
          return rated ? `${item.source}-${item.source_id}` : null
        } catch {
          return null
        }
      }),
    ).then((results) => {
      setRatedKeys(new Set(results.filter(Boolean) as string[]))
    })
  }, [activeTab, items])

  // ── Reschedule ─────────────────────────────────────────────────────────────

  const handleReschedule = async () => {
    if (!rescheduleTarget || !newDate || !newTime) return
    setRescheduling(true)
    try {
      const isoDate = new Date(`${newDate}T${newTime}:00`).toISOString()
      if (rescheduleTarget.source === 'booking') {
        await sessionsApi.rescheduleBooking(rescheduleTarget.source_id, isoDate)
      } else {
        await sessionsApi.rescheduleSession(rescheduleTarget.source_id, isoDate)
      }
      setRescheduleTarget(null)
      fetchTab(activeTab)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reschedule')
    } finally {
      setRescheduling(false)
    }
  }

  // ── Cancel ─────────────────────────────────────────────────────────────────

  const handleCancel = async () => {
    if (!cancelTarget) return
    setCancelling(true)
    try {
      if (cancelTarget.source === 'booking') {
        await sessionsApi.cancelBooking(cancelTarget.source_id, 'Cancelled by client')
      } else {
        await sessionsApi.cancelSession(cancelTarget.source_id, 'Cancelled by client')
      }
      setCancelTarget(null)
      fetchTab(activeTab)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel')
    } finally {
      setCancelling(false)
    }
  }

  // ── Rating ─────────────────────────────────────────────────────────────────

  const openRating = (item: InterviewItem) => {
    setRateTarget(item)
    setRating(0)
    setHoverRating(0)
    setRatingComment('')
    setRatingError('')
  }

  const handleSubmitRating = async () => {
    if (!rateTarget || rating === 0) return
    setSubmittingRating(true)
    setRatingError('')
    try {
      await sessionsApi.submitRating(
        rateTarget.source,
        rateTarget.source_id,
        rating,
        ratingComment || undefined,
      )
      setRatedKeys((prev) => new Set([...prev, `${rateTarget.source}-${rateTarget.source_id}`]))
      setRateTarget(null)
    } catch (err) {
      setRatingError(err instanceof Error ? err.message : 'Failed to submit rating')
    } finally {
      setSubmittingRating(false)
    }
  }

  // ── Recording ──────────────────────────────────────────────────────────────

  const openRecording = async (item: InterviewItem) => {
    setRecordingTarget(item)
    setRecordingData(null)
    setRecordingError('')
    setRecordingLoading(true)
    try {
      const res =
        item.source === 'booking'
          ? await sessionsApi.getBookingRecording(item.source_id)
          : await sessionsApi.getRecording(item.source_id)
      setRecordingData(res.data ?? res)
    } catch {
      setRecordingError('Recording is not available yet. Please check back after the session ends.')
    } finally {
      setRecordingLoading(false)
    }
  }

  // ── Card renderer ──────────────────────────────────────────────────────────

  const renderCard = (item: InterviewItem) => {
    const { day, month, time } = fmtDate(item.scheduled_at)
    const color = STATUS_COLOR[item.status] || '#6b7280'
    const label = STATUS_LABEL[item.status] || item.status
    const isUpcoming = activeTab === 'upcoming'
    const isCompleted = activeTab === 'completed'
    const ratedKey = `${item.source}-${item.source_id}`
    const isRated = ratedKeys.has(ratedKey)
    const msUntilMeeting = new Date(item.scheduled_at).getTime() - Date.now()
    const canJoin = !!item.zoom_join_url && msUntilMeeting <= 45 * 60 * 1000

    return (
      <div key={ratedKey} className="ci-card">
        {/* Date badge + status */}
        <div className="ci-date-col">
          <div className="ci-date-badge">
            <span className="ci-day">{day}</span>
            <span className="ci-month">{month}</span>
          </div>
          <span className="ci-status-pill" style={{ background: `${color}18`, color }}>
            {label}
          </span>
        </div>

        {/* Details */}
        <div className="ci-details">
          <h3 className="ci-title">{item.title}</h3>
          <div className="ci-meta">
            <span><Clock size={13} />{time}</span>
            <span>{item.duration_minutes} min</span>
          </div>

          {item.interviewer_alias && (
            <div className="ci-interviewer">
              <div className="ci-avatar">{item.interviewer_alias.charAt(0).toUpperCase()}</div>
              <div>
                <p className="ci-iv-name">{item.interviewer_alias}</p>
                <p className="ci-iv-role">Interview Coach</p>
              </div>
            </div>
          )}

          {isUpcoming && item.zoom_join_url && (
            <div className="ci-zoom-badge">
              <Video size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
              <span style={{ color: '#16a34a', fontWeight: 500 }}>Zoom Ready</span>
              {item.zoom_password && (
                <span className="ci-zoom-pwd">
                  Password: <strong>{item.zoom_password}</strong>
                </span>
              )}
            </div>
          )}

          {item.cancellation_reason && (
            <div className="ci-cancel-reason">
              <AlertCircle size={13} />
              <span>{item.cancellation_reason}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="ci-actions">
          {isUpcoming && (
            <>
              {canJoin ? (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/client/interviews/${item.source}/${item.source_id}`)}
                >
                  <Video size={15} /> Join
                </button>
              ) : (
                <button className="btn btn-primary" disabled style={{ opacity: 0.4 }}>
                  <Video size={15} /> Join
                </button>
              )}
              {item.source === 'booking' && (
                <button className="btn btn-outline" onClick={() => navigate(`/client/schedule?rescheduleId=${item.source_id}`)}>
                  <RefreshCcw size={14} /> Reschedule
                </button>
              )}
              <button
                className="btn btn-outline ci-cancel-btn"
                onClick={() => setCancelTarget(item)}
              >
                <XCircle size={14} /> Cancel
              </button>
            </>
          )}

          {isCompleted && (
            <>
              <button
                className="btn btn-outline"
                onClick={() => openRecording(item)}
              >
                <Film size={14} /> View Recording
              </button>

              {isRated ? (
                <span className="ci-rated-badge">
                  <CheckCircle size={13} /> Rated
                </span>
              ) : (
                <button className="btn btn-outline ci-rate-btn" onClick={() => openRating(item)}>
                  <Star size={14} /> Rate Interviewer
                </button>
              )}

              <button
                className="btn btn-outline ci-feedback-btn"
                onClick={() => navigate('/client/feedback')}
              >
                <MessageSquare size={14} /> View Feedback
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div className="client-interviews">
      <div className="page-header">
        <h1 className="page-title">My Interviews</h1>
        <p className="page-subtitle">View and manage your interview sessions</p>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          <Calendar size={15} />
          Sessions (Upcoming)
          {counts.upcoming > 0 && <span className="tab-count">{counts.upcoming}</span>}
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={15} />
          Sessions (Completed)
          {counts.completed > 0 && <span className="tab-count">{counts.completed}</span>}
        </button>
      </div>

      {loading ? (
        <LoadingSpinner message="Loading interviews..." />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={activeTab === 'upcoming' ? 'No upcoming interviews' : 'No completed interviews'}
          message={
            activeTab === 'upcoming'
              ? 'Book a mock interview to see it here'
              : 'Completed interviews will appear here'
          }
          actionLabel="Schedule a Session"
          onAction={() => navigate('/client/schedule')}
        />
      ) : (
        <div className="ci-list">{items.map(renderCard)}</div>
      )}

      {/* ── Reschedule Modal ──────────────────────────────────────────────── */}
      <Modal
        open={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        title="Reschedule Interview"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setRescheduleTarget(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleReschedule}
              disabled={rescheduling || !newDate || !newTime}
            >
              {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
            </button>
          </>
        }
      >
        <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
          Select a new date and time for your session.
        </p>
        {rescheduleTarget && (
          <div className="ci-current-time">
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Current:</span>
            <span>
              {fmtDate(rescheduleTarget.scheduled_at).full} at{' '}
              {fmtDate(rescheduleTarget.scheduled_at).time}
            </span>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">New Date</label>
          <input
            type="date" className="form-input" value={newDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setNewDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">New Time</label>
          <input type="time" className="form-input" value={newTime}
            onChange={(e) => setNewTime(e.target.value)} />
        </div>
      </Modal>

      {/* ── Cancel Modal ──────────────────────────────────────────────────── */}
      <Modal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title="Cancel Interview"
        maxWidth={440}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setCancelTarget(null)}>
              Keep Interview
            </button>
            <button className="btn btn-danger" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <XCircle size={48} style={{ color: '#dc2626', marginBottom: 16 }} />
          <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
            Are you sure you want to cancel this interview?
          </p>
          <p style={{ fontSize: 14, color: 'var(--text-dark)', fontWeight: 500 }}>
            {cancelTarget?.title}
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 10 }}>
            This action cannot be undone. Your slot will be released.
          </p>
        </div>
      </Modal>

      {/* ── Rate Interviewer Modal ────────────────────────────────────────── */}
      <Modal
        open={!!rateTarget}
        onClose={() => setRateTarget(null)}
        title="Rate Your Interviewer"
        maxWidth={480}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setRateTarget(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitRating}
              disabled={submittingRating || rating === 0}
            >
              {submittingRating ? 'Submitting...' : 'Submit Rating'}
            </button>
          </>
        }
      >
        {rateTarget?.interviewer_alias && (
          <div className="ci-rate-header">
            <div className="ci-rate-avatar">
              {rateTarget.interviewer_alias.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="ci-rate-name">{rateTarget.interviewer_alias}</p>
              <p className="ci-rate-session">{rateTarget.title}</p>
            </div>
          </div>
        )}

        <p style={{ color: 'var(--text-muted)', marginBottom: 4, fontSize: 14 }}>
          How was your overall experience?
        </p>

        <div className="ci-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              className="ci-star-btn"
              onClick={() => setRating(s)}
              onMouseEnter={() => setHoverRating(s)}
              onMouseLeave={() => setHoverRating(0)}
            >
              <Star
                size={36}
                fill={s <= (hoverRating || rating) ? '#d4af37' : 'none'}
                stroke={s <= (hoverRating || rating) ? '#d4af37' : '#cbd5e1'}
              />
            </button>
          ))}
        </div>
        <div className="ci-star-labels">
          {['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'].map((l) => (
            <span key={l}>{l}</span>
          ))}
        </div>
        {rating > 0 && (
          <div className="ci-star-selected">
            {STAR_LABELS[rating]} — {rating} / 5 stars
          </div>
        )}

        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label">
            Additional Comments{' '}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(Optional)</span>
          </label>
          <textarea
            className="form-input"
            rows={3}
            placeholder="Share your experience — what went well, what could be improved..."
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {ratingError && (
          <div style={{
            padding: '10px 14px', background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8,
            color: '#dc2626', fontSize: 13, marginTop: 8,
          }}>
            {ratingError}
          </div>
        )}
      </Modal>

      {/* ── Recording Modal ───────────────────────────────────────────────── */}
      <Modal
        open={!!recordingTarget}
        onClose={() => { setRecordingTarget(null); setRecordingData(null); setRecordingError('') }}
        title="Session Recording"
        maxWidth={720}
        footer={
          <button
            className="btn btn-outline"
            onClick={() => { setRecordingTarget(null); setRecordingData(null); setRecordingError('') }}
          >
            Close
          </button>
        }
      >
        {recordingLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            Loading recording…
          </div>
        )}

        {!recordingLoading && recordingError && (
          <div className="ci-rec-unavailable">
            <Film size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Recording Not Available</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{recordingError}</p>
          </div>
        )}

        {!recordingLoading && recordingData && (
          <div className="ci-rec-body">
            <video
              className="ci-rec-video"
              src={recordingData.recording_url}
              controls
              controlsList="nodownload"
            />
            {recordingData.duration_seconds != null && (
              <p className="ci-rec-duration">
                <Clock size={13} />
                Duration: {Math.floor(recordingData.duration_seconds / 60)}m {recordingData.duration_seconds % 60}s
              </p>
            )}
            {recordingData.transcript_url && (
              <a
                href={recordingData.transcript_url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline ci-rec-transcript-btn"
              >
                <MessageSquare size={14} /> View Transcript
              </a>
            )}
          </div>
        )}
      </Modal>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .ci-list { display: flex; flex-direction: column; gap: 16px; }

  .ci-card {
    display: flex; gap: 24px; align-items: flex-start;
    background: white; border-radius: 16px; padding: 24px;
    box-shadow: var(--shadow); border: 1px solid var(--border);
  }

  .ci-date-col {
    display: flex; flex-direction: column; align-items: center; gap: 8px; flex-shrink: 0;
  }
  .ci-date-badge {
    width: 64px; height: 64px; background: var(--primary-navy);
    color: var(--accent-cream, #f5f0dc); border-radius: 12px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .ci-day  { font-size: 24px; font-weight: 700; line-height: 1; }
  .ci-month{ font-size: 11px; font-weight: 500; }

  .ci-status-pill {
    font-size: 11px; font-weight: 600; padding: 3px 8px;
    border-radius: 4px; white-space: nowrap;
  }

  .ci-details { flex: 1; min-width: 0; }
  .ci-title { font-size: 17px; font-weight: 600; margin-bottom: 6px; }

  .ci-meta {
    display: flex; gap: 16px; color: var(--text-muted);
    font-size: 13px; margin-bottom: 12px;
  }
  .ci-meta span { display: flex; align-items: center; gap: 5px; }

  .ci-interviewer {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 10px; width: fit-content; margin-bottom: 10px;
  }
  .ci-avatar {
    width: 36px; height: 36px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; flex-shrink: 0;
  }
  .ci-iv-name { font-size: 13px; font-weight: 500; }
  .ci-iv-role { font-size: 11px; color: var(--text-muted); }

  .ci-zoom-badge {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; background: rgba(34,197,94,0.06);
    border: 1px solid rgba(34,197,94,0.2); border-radius: 8px;
    font-size: 13px; margin-top: 4px; width: fit-content;
  }
  .ci-zoom-pwd { color: var(--text-muted); margin-left: 6px; }

  .ci-cancel-reason {
    display: flex; align-items: flex-start; gap: 6px; font-size: 12px;
    color: #dc2626; margin-top: 6px; padding: 6px 10px;
    background: rgba(220,38,38,0.05); border-radius: 6px; width: fit-content;
  }

  .ci-actions {
    display: flex; flex-direction: column; gap: 8px;
    align-items: flex-end; justify-content: center; flex-shrink: 0;
  }

  .ci-cancel-btn   { border-color: #dc2626 !important; color: #dc2626 !important; }
  .ci-cancel-btn:hover { background: #dc2626 !important; color: white !important; }

  .ci-rate-btn   { border-color: #d4af37 !important; color: #b8960c !important; }
  .ci-rate-btn:hover { background: #d4af37 !important; color: white !important; }

  .ci-feedback-btn { border-color: #7c3aed !important; color: #7c3aed !important; }
  .ci-feedback-btn:hover { background: #7c3aed !important; color: white !important; }

  .ci-rated-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; background: rgba(212,175,55,0.1);
    color: #b8960c; border-radius: 8px; font-size: 13px; font-weight: 500;
  }

  .tab-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 20px; padding: 0 6px;
    background: var(--primary-navy); color: white;
    border-radius: 10px; font-size: 11px; font-weight: 700; margin-left: 4px;
  }
  .tab.active .tab-count { background: white; color: var(--primary-navy); }

  .ci-current-time {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 8px; margin-bottom: 16px; font-size: 13px;
  }

  .ci-rate-header {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 16px; background: var(--accent-light,#f8f9fa);
    border-radius: 10px; margin-bottom: 20px;
  }
  .ci-rate-avatar {
    width: 48px; height: 48px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 20px; font-weight: 700; flex-shrink: 0;
  }
  .ci-rate-name    { font-size: 16px; font-weight: 600; }
  .ci-rate-session { font-size: 12px; color: var(--text-muted); margin-top: 2px; }

  .ci-stars {
    display: flex; justify-content: center; gap: 4px; margin: 16px 0 4px;
  }
  .ci-star-btn {
    background: none; border: none; padding: 4px; cursor: pointer; transition: transform 0.15s;
  }
  .ci-star-btn:hover { transform: scale(1.2); }

  .ci-star-labels {
    display: flex; justify-content: space-between;
    font-size: 11px; color: var(--text-muted); padding: 0 4px; margin-bottom: 4px;
  }
  .ci-star-selected {
    text-align: center; font-size: 13px; font-weight: 600; color: #b8960c;
    padding: 6px; background: rgba(212,175,55,0.08); border-radius: 6px;
  }

  .ci-rec-unavailable {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center;
  }

  .ci-rec-body { display: flex; flex-direction: column; gap: 14px; }

  .ci-rec-video {
    width: 100%; border-radius: 10px; background: #000;
    max-height: 400px; outline: none;
  }

  .ci-rec-duration {
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; color: var(--text-muted);
  }

  .ci-rec-transcript-btn { align-self: flex-start; }

  .btn-danger { background: #dc2626; border-color: #dc2626; color: white; }
  .btn-danger:hover { background: #b91c1c; }
  .btn-danger:disabled { background: #fca5a5; border-color: #fca5a5; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }

  @media (max-width: 768px) {
    .ci-card { flex-direction: column; }
    .ci-date-col { flex-direction: row; }
    .ci-actions { flex-direction: row; flex-wrap: wrap; justify-content: flex-start; }
  }
`
